import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { PowerLevelBadge, PowerLevelInline } from '../components/PowerLevelBadge';
import { StreakFire } from '../components/TrophyCase';
import { matchesAPI, playersAPI, tournamentsAPI, rankingsAPI, clubsAPI, membershipsAPI } from '../lib/api';
import { formatDate, formatDuration, getStatusColor, getInitials } from '../lib/utils';
import { getPowerTier } from '../lib/powerLevel';
import {
  Activity, Users, Trophy, Target, TrendingUp, Clock,
  ChevronRight, Award, Zap, CreditCard, Building,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [matches, setMatches] = useState([]);
  const [liveMatches, setLiveMatches] = useState([]);
  const [playerStats, setPlayerStats] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [topRankings, setTopRankings] = useState([]);
  const [clubStats, setClubStats] = useState(null);

  const isAdmin = ['platform_admin', 'club_admin'].includes(user?.role);
  const ghostMode = localStorage.getItem('ghost-mode') || 'player';
  const showAdminView = isAdmin && ghostMode === 'manage';

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const promises = [
          matchesAPI.getAll(),
          playersAPI.getById(user?.user_id),
          tournamentsAPI.getAll(),
          rankingsAPI.getAll(),
        ];
        if (showAdminView) promises.push(clubsAPI.getAll());

        const results = await Promise.all(promises);
        const allMatches = results[0].data || [];
        setMatches(allMatches.slice(0, 5));
        setLiveMatches(allMatches.filter(m => m.status === 'live'));
        setPlayerStats(results[1].data?.stats);
        setTournaments((results[2].data || []).slice(0, 3));
        setTopRankings((results[3].data || []).slice(0, 5));

        if (showAdminView && results[4]) {
          const clubs = results[4].data || [];
          setClubStats({ totalClubs: clubs.length, totalMembers: clubs.reduce((s, c) => s + (c.member_ids?.length || 0), 0) });
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    if (user?.user_id) fetchDashboardData();
  }, [user, showAdminView]);

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
          </div>
        </div>
      </Layout>
    );
  }

  const elo = playerStats?.elo_rating || 1200;
  const tier = getPowerTier(elo);
  const winRate = playerStats?.matches_played ? Math.round((playerStats.matches_won / playerStats.matches_played) * 100) : 0;

  return (
    <Layout>
      <div className="space-y-4 sm:space-y-6" data-testid="dashboard-page">
        {/* Welcome Header with Power Level */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <PowerLevelBadge elo={elo} size="md" />
            <div className="min-w-0">
              <h1 className="font-heading text-lg sm:text-2xl font-bold uppercase tracking-tight truncate">
                {t('dashboard.welcome')}, {user?.name?.split(' ')[0]}!
              </h1>
              <p className="text-xs sm:text-sm text-muted-foreground">{t('dashboard.subtitle')}</p>
              {playerStats?.current_streak >= 3 && (
                <div className="flex items-center gap-1 mt-0.5">
                  <StreakFire currentStreak={playerStats.current_streak} />
                  <span className="text-[10px] text-muted-foreground">{playerStats.current_streak} win streak</span>
                </div>
              )}
            </div>
          </div>
          <Link to="/matches/new" className="shrink-0">
            <Button size="sm" className="bg-secondary hover:bg-secondary/90 h-9" data-testid="new-match-btn">
              <Target className="mr-1.5 h-4 w-4" /><span className="hidden sm:inline">{t('dashboard.startMatch')}</span><span className="sm:hidden">Play</span>
            </Button>
          </Link>
        </div>

        {/* Live Matches Alert */}
        {liveMatches.length > 0 && (
          <Card className="border-red-500/50 bg-red-500/5">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-3 w-3 rounded-full bg-red-500 live-indicator" />
                  <span className="font-bold text-red-500">
                    {liveMatches.length} {t('dashboard.liveMatches')}{liveMatches.length > 1 ? 'es' : ''} {t('dashboard.inProgress')}
                  </span>
                </div>
                <Link to="/matches?status=live">
                  <Button variant="outline" size="sm" className="border-red-500 text-red-500">{t('dashboard.viewLive')}<ChevronRight className="ml-1 h-4 w-4" /></Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Admin Financial Overview (Ghost/Manage Mode) */}
        {showAdminView && clubStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-overview">
            <Card className="card-neon"><CardContent className="p-4 text-center"><Building className="h-6 w-6 mx-auto mb-2 text-primary" /><p className="text-xl font-bold">{clubStats.totalClubs}</p><p className="text-xs text-muted-foreground">{t('dashboard.activeClubs')}</p></CardContent></Card>
            <Card className="card-neon"><CardContent className="p-4 text-center"><Users className="h-6 w-6 mx-auto mb-2 text-secondary" /><p className="text-xl font-bold">{clubStats.totalMembers}</p><p className="text-xs text-muted-foreground">{t('dashboard.totalMembers')}</p></CardContent></Card>
            <Card className="card-neon"><CardContent className="p-4 text-center"><Trophy className="h-6 w-6 mx-auto mb-2 text-accent" /><p className="text-xl font-bold">{tournaments.length}</p><p className="text-xs text-muted-foreground">{t('nav.tournaments')}</p></CardContent></Card>
            <Card className="card-neon"><CardContent className="p-4 text-center"><Activity className="h-6 w-6 mx-auto mb-2 text-purple-500" /><p className="text-xl font-bold">{matches.length + liveMatches.length}</p><p className="text-xs text-muted-foreground">{t('nav.matches')}</p></CardContent></Card>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4" data-testid="stats-grid">
          {[
            { label: t('dashboard.matchesPlayed'), value: playerStats?.matches_played || 0, icon: Activity, color: 'text-primary', bg: 'bg-primary/10' },
            { label: t('dashboard.winRate'), value: `${winRate}%`, icon: TrendingUp, color: 'text-secondary', bg: 'bg-secondary/10' },
            { label: t('dashboard.eloRating'), value: <PowerLevelInline elo={elo} />, icon: Award, color: 'text-accent', bg: 'bg-accent/10' },
            { label: t('dashboard.bestStreak'), value: <span className="flex items-center gap-1">{playerStats?.best_streak || 0}<StreakFire currentStreak={playerStats?.best_streak || 0} /></span>, icon: Zap, color: 'text-purple-500', bg: 'bg-purple-500/10' },
          ].map((stat) => {
            const Icon = stat.icon;
            return (
              <Card key={stat.label} className="hover:shadow-md transition-shadow">
                <CardContent className="p-3 sm:p-6">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-[10px] sm:text-sm text-muted-foreground truncate">{stat.label}</p>
                      <div className="text-base sm:text-2xl font-bold font-heading mt-0.5">{stat.value}</div>
                    </div>
                    <div className={`p-2 sm:p-3 rounded-full ${stat.bg} shrink-0`}>
                      <Icon className={`h-5 w-5 sm:h-6 sm:w-6 ${stat.color}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Main Content Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Recent Matches */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="font-heading uppercase tracking-tight text-base">{t('dashboard.recentMatches')}</CardTitle>
              <Link to="/matches"><Button variant="ghost" size="sm" className="text-xs">{t('dashboard.viewAll')}<ChevronRight className="ml-1 h-3.5 w-3.5" /></Button></Link>
            </CardHeader>
            <CardContent>
              {matches.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Activity className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <p className="text-sm">{t('dashboard.noMatches')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {matches.map(match => (
                    <Link key={match.match_id} to={`/matches/${match.match_id}`} className="block">
                      <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                        <div className="flex items-center gap-3">
                          <Badge className={`${getStatusColor(match.status)} text-[10px]`}>{match.status}</Badge>
                          <div>
                            <p className="font-medium text-sm">{match.match_type === 'singles' ? 'Singles' : 'Doubles'}</p>
                            <p className="text-xs text-muted-foreground">{formatDate(match.created_at)}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-mono font-bold text-sm">{match.games?.map(g => `${g.player1_score}-${g.player2_score}`).join(', ')}</p>
                          {match.duration_seconds > 0 && (
                            <p className="text-xs text-muted-foreground flex items-center justify-end gap-1"><Clock className="h-3 w-3" />{formatDuration(match.duration_seconds)}</p>
                          )}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top Rankings with Power Level */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="font-heading uppercase tracking-tight text-base">{t('dashboard.topRankings')}</CardTitle>
              <Link to="/rankings"><Button variant="ghost" size="sm" className="text-xs">{t('dashboard.viewAll')}<ChevronRight className="ml-1 h-3.5 w-3.5" /></Button></Link>
            </CardHeader>
            <CardContent>
              {topRankings.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Trophy className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <p className="text-sm">{t('dashboard.noRankings')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {topRankings.map((player, index) => {
                    const pTier = getPowerTier(player.rating || 1200);
                    return (
                      <div key={player.player_id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                        <div className="flex items-center gap-3">
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs ${
                            index === 0 ? 'bg-yellow-500 text-black' : index === 1 ? 'bg-gray-400 text-black' : index === 2 ? 'bg-orange-500 text-white' : 'bg-muted text-muted-foreground'
                          }`}>{index + 1}</div>
                          <Avatar className="h-7 w-7">
                            <AvatarImage src={player.picture} />
                            <AvatarFallback className="text-xs">{getInitials(player.name)}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-sm">{player.name}</p>
                            <p className="text-xs text-muted-foreground">{player.matches_played} {t('rankings.matches')} - {player.win_rate}% {t('rankings.winRateLabel')}</p>
                          </div>
                        </div>
                        <PowerLevelInline elo={player.rating || 1200} />
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Tournaments */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="font-heading uppercase tracking-tight text-base">{t('dashboard.upcomingTournaments')}</CardTitle>
            <Link to="/tournaments"><Button variant="ghost" size="sm" className="text-xs">{t('dashboard.viewAll')}<ChevronRight className="ml-1 h-3.5 w-3.5" /></Button></Link>
          </CardHeader>
          <CardContent>
            {tournaments.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Trophy className="h-10 w-10 mx-auto mb-3 opacity-40" />
                <p className="text-sm">{t('dashboard.noTournaments')}</p>
                <Link to="/tournaments/new" className="mt-3 inline-block"><Button variant="outline" size="sm">{t('tournaments.create')}</Button></Link>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {tournaments.map(tournament => (
                  <Link key={tournament.tournament_id} to={`/tournaments/${tournament.tournament_id}`} className="block">
                    <div className="p-4 rounded-lg border border-border hover:border-primary/50 hover:shadow-md transition-all">
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="outline" className="capitalize text-[10px]">{tournament.format?.replace('_', ' ')}</Badge>
                        <Badge className={`text-[10px] ${tournament.status === 'registration' ? 'bg-blue-500' : tournament.status === 'in_progress' ? 'bg-green-500' : 'bg-gray-500'}`}>{tournament.status}</Badge>
                      </div>
                      <h3 className="font-heading font-bold mb-2">{tournament.name}</h3>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{tournament.participant_ids?.length || 0}/{tournament.max_participants}</span>
                        {tournament.start_date && <span>{formatDate(tournament.start_date)}</span>}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
